import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type { PumpThroughput, RecordThroughputBody, RevokeThroughputBody } from './pump-throughput.types';

const BASE = '/v1/devices/pump-throughput';

/** Every pump's current figure at this site — one round trip for the screen. */
export function usePumpThroughput(enabled = true) {
  return useQuery({
    queryKey: ['pump-throughput'],
    queryFn: () => api.get<PumpThroughput[]>(BASE),
    staleTime: 30_000,
    enabled,
  });
}

/** One pump's trail, newest first, revocations included. */
export function usePumpThroughputHistory(mac: string | null, actuator: string | null) {
  return useQuery({
    queryKey: ['pump-throughput-history', mac, actuator],
    queryFn: () => api.get<PumpThroughput[]>(`${BASE}/${mac}/${actuator}`),
    enabled: !!mac && !!actuator,
  });
}

export function useRecordPumpThroughput() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordThroughputBody) => api.post<PumpThroughput>(BASE, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pump-throughput'] });
      void qc.invalidateQueries({ queryKey: ['pump-throughput-history'] });
    },
  });
}

/** Withdraw a figure — the pump goes back to having none. An APPEND, so the
 *  trail keeps who stopped standing behind a number, when, and why. */
export function useRevokePumpThroughput() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RevokeThroughputBody) => api.post<PumpThroughput>(`${BASE}/revoke`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pump-throughput'] });
      void qc.invalidateQueries({ queryKey: ['pump-throughput-history'] });
    },
  });
}
